import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  domError,
  errorText,
  formatError,
  isRecord,
  result,
  validation,
  validationError,
  withTimeout,
  workerValue,
} from './shared.ts';

async function sharedWorkerOutcome(): Promise<RawData> {
  if (!('SharedWorker' in window)) {
    return { available: false };
  }
  try {
    const message = await withTimeout(
      new Promise<string>((resolve, reject) => {
        const worker = new SharedWorker('/client/shared-worker.js', {
          type: 'module',
        });
        worker.port.start();
        worker.port.addEventListener(
          'message',
          (event: MessageEvent<unknown>) => {
            resolve(String(event.data));
            worker.port.close();
          },
        );
        worker.addEventListener('error', (event) =>
          reject(new Error(event.message)),
        );
        worker.port.postMessage('ping');
      }),
      4000,
      'SharedWorker timed out',
    );
    return {
      messages: [message],
    };
  } catch (error) {
    const detail = domError(error);
    return {
      errorName: detail.name,
      errorMessage: detail.message,
    };
  }
}

async function pingServiceWorker(worker: ServiceWorker): Promise<string> {
  const channel = new MessageChannel();
  try {
    const message = await withTimeout(
      new Promise<string>((resolve, reject) => {
        channel.port1.addEventListener(
          'message',
          (event: MessageEvent<unknown>) => resolve(String(event.data)),
          { once: true },
        );
        channel.port1.addEventListener(
          'messageerror',
          () => reject(new Error('ServiceWorker message could not be read')),
          { once: true },
        );
        channel.port1.start();
        worker.postMessage('ping', [channel.port2]);
      }),
      3000,
      'ServiceWorker timed out',
    );
    return message;
  } finally {
    channel.port1.close();
    channel.port2.close();
  }
}

async function currentServiceWorker(
  registration: ServiceWorkerRegistration,
): Promise<ServiceWorker> {
  const updatedRegistration = await registration.update();
  const worker =
    updatedRegistration.installing ??
    updatedRegistration.waiting ??
    updatedRegistration.active;
  if (!worker) {
    throw new Error('ServiceWorker did not install');
  }
  if (worker.state == 'activated') {
    return worker;
  }
  if (worker.state == 'redundant') {
    throw new Error('ServiceWorker became redundant');
  }
  return withTimeout(
    new Promise<ServiceWorker>((resolve, reject) => {
      const stateChange = (): void => {
        if (worker.state == 'activated') {
          worker.removeEventListener('statechange', stateChange);
          resolve(worker);
        } else if (worker.state == 'redundant') {
          worker.removeEventListener('statechange', stateChange);
          reject(new Error('ServiceWorker became redundant'));
        }
      };
      worker.addEventListener('statechange', stateChange);
    }),
    5000,
    'ServiceWorker activation timed out',
  );
}

async function collect(): Promise<RawData> {
  const dedicatedWorker = { message: await workerValue("'pong'") };
  const sharedWorker = await sharedWorkerOutcome();
  let serviceWorker: RawData;
  try {
    if (!('serviceWorker' in navigator)) {
      serviceWorker = { available: false };
    } else {
      const registration = await navigator.serviceWorker.register(
        '/client/service-worker.js',
        { scope: '/', type: 'module' },
      );
      const activeWorker = await currentServiceWorker(registration);
      serviceWorker = {
        state: activeWorker.state,
        message: await pingServiceWorker(activeWorker),
        controlled: Boolean(navigator.serviceWorker.controller),
      };
    }
  } catch (error) {
    const detail = domError(error);
    serviceWorker = {
      errorName: detail.name,
      errorMessage: detail.message,
    };
  }
  const messageChannel = await new Promise<RawData>((resolve) => {
    const channel = new MessageChannel();
    channel.port1.onmessage = (event) => {
      resolve({
        message: event.data,
      });
      channel.port1.close();
      channel.port2.close();
    };
    channel.port2.onmessage = () => channel.port2.postMessage('pong');
    channel.port1.postMessage('ping');
  });
  const worklets = {
    audio: 'audioWorklet' in AudioContext.prototype,
    paint: 'paintWorklet' in CSS,
  };
  return {
    dedicatedWorker,
    sharedWorker,
    serviceWorker,
    messageChannel,
    worklets,
  };
}

function validateWorkers(rawData: RawData): ValidationResult {
  const reasons: string[] = [];
  for (const key of ['dedicatedWorker', 'messageChannel']) {
    const message = isRecord(rawData[key]) ? rawData[key].message : null;
    if (message != 'pong') {
      reasons.push(`${key} did not return pong`);
    }
  }
  const sharedWorker = isRecord(rawData.sharedWorker)
    ? rawData.sharedWorker
    : {};
  if (
    !Array.isArray(sharedWorker.messages) ||
    sharedWorker.messages[0] != 'pong'
  ) {
    reasons.push('sharedWorker did not return pong');
  }
  const serviceWorker = isRecord(rawData.serviceWorker)
    ? rawData.serviceWorker
    : {};
  if (serviceWorker.message != 'pong') {
    reasons.push('serviceWorker did not return pong');
  }
  return validation(reasons);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  const shared = isRecord(rawData.sharedWorker)
    ? Array.isArray(rawData.sharedWorker.messages) &&
      rawData.sharedWorker.messages[0] == 'pong'
      ? 'success'
      : 'failed'
    : 'invalid';
  return `SharedWorker: ${String(shared)}`;
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateWorkers(rawData);
}

export const workerFunctionalityTest: TestDefinition = {
  id: 'workerFunctionality',
  title: 'Worker functionality',
  category: 'shared',
  format,
  run,
  validate,
};
